import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import AppShell from './AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2`;

const ROLE_LABELS = {
  Admin: { label: 'Administrator', color: 'bg-purple-500/20 text-purple-400 border border-purple-500/30' },
  PowerUser: { label: 'Superbruker', color: 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' },
  Member: { label: 'Medlem', color: 'bg-slate-700/50 text-slate-400' },
};

function UserManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'Member' });
  const [creating, setCreating] = useState(false);
  const [systemStats, setSystemStats] = useState(null);
  const [isSystemView, setIsSystemView] = useState(false);

  useEffect(() => {
    fetchPermissions();
  }, []);

  useEffect(() => {
    if (permissions?.is_admin || permissions?.is_power_user || permissions?.is_system_admin) {
      fetchUsers();
      if (permissions?.is_system_admin) {
        fetchSystemStats();
      }
    }
  }, [permissions]);

  const fetchPermissions = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(
        `${API}/ops/users/me/permissions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPermissions(response.data);
      
      if (!response.data.is_admin && !response.data.is_power_user && !response.data.is_system_admin) {
        toast.error('Du har ikke tilgang til brukeradministrasjon');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      navigate('/dashboard');
    }
  };

  const fetchSystemStats = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(
        `${API}/ops/system/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSystemStats(response.data);
    } catch (error) {
      console.error('Failed to fetch system stats:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(
        `${API}/ops/users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(response.data.users);
      setIsSystemView(response.data.is_system_view || false);
      setUsers(response.data.users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Kunne ikke hente brukere');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.name || !newUser.email) {
      toast.error('Fyll ut navn og e-post');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.post(
        `${API}/ops/users`,
        newUser,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Bruker opprettet! Midlertidig passord: ${response.data.temp_password}`, { duration: 10000 });
      setShowAddModal(false);
      setNewUser({ name: '', email: '', role: 'Member' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke opprette bruker');
    } finally {
      setCreating(false);
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.patch(
        `${API}/ops/users/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rolle oppdatert');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke endre rolle');
    }
  };

  const handleDeactivate = async (userId, userName) => {
    if (!window.confirm(`Er du sikker på at du vil deaktivere ${userName}?`)) return;
    
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.delete(
        `${API}/ops/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Bruker deaktivert');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke deaktivere bruker');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <AppShell permissions={permissions}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-white">
              {permissions?.is_system_admin ? 'Systemadministrasjon' : 'Brukere'}
            </h1>
            {permissions?.is_system_admin && (
              <span className="px-2 py-0.5 bg-rose-500/20 text-rose-400 text-xs font-medium rounded-full border border-rose-500/30">
                System Admin
              </span>
            )}
          </div>
          {(permissions?.is_admin || permissions?.is_system_admin) && (
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-cyan-500 text-slate-950 rounded-lg text-sm font-semibold hover:bg-cyan-400 transition-colors shadow-lg shadow-cyan-500/25"
              data-testid="add-user-btn"
            >
              + Legg til
            </button>
          )}
        </div>

        {/* System Admin Stats Panel */}
        {permissions?.is_system_admin && systemStats && (
          <div className="bg-gradient-to-r from-rose-600/20 to-rose-700/20 border border-rose-500/30 rounded-2xl p-6 mb-6">
            <h2 className="text-lg font-semibold text-white mb-4">Systemoversikt</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-900/50 rounded-xl p-4">
                <div className="text-3xl font-bold text-white">{systemStats.organizations}</div>
                <div className="text-sm text-slate-400">Organisasjoner</div>
              </div>
              <div className="bg-slate-900/50 rounded-xl p-4">
                <div className="text-3xl font-bold text-white">{systemStats.users}</div>
                <div className="text-sm text-slate-400">Brukere</div>
              </div>
              <div className="bg-slate-900/50 rounded-xl p-4">
                <div className="text-3xl font-bold text-white">{systemStats.tasks?.total || 0}</div>
                <div className="text-sm text-slate-400">Oppgaver</div>
              </div>
              <div className="bg-slate-900/50 rounded-xl p-4">
                <div className="text-3xl font-bold text-white">{systemStats.deviations?.open || 0}</div>
                <div className="text-sm text-slate-400">Åpne avvik</div>
              </div>
            </div>
          </div>
        )}

        {/* System View Banner */}
        {isSystemView && (
          <div className="mb-4 px-4 py-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-sm text-amber-400">
            Du ser alle brukere på tvers av organisasjoner
          </div>
        )}

        {/* User List */}
        <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-2xl divide-y divide-slate-800/50">
          {users.map((u) => (
            <div key={u.id} className="p-4 flex items-center justify-between" data-testid={`user-row-${u.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center">
                  <span className="text-slate-300 font-medium">
                    {u.name?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
                <div>
                  <div className="font-medium text-white flex items-center gap-2">
                    {u.name}
                    {u.id === user?.id && (
                      <span className="text-xs text-slate-500">(deg)</span>
                    )}
                  </div>
                  <div className="text-sm text-slate-400">
                    {u.email}
                    {isSystemView && u.organization_name && (
                      <span className="ml-2 text-xs text-slate-500">• {u.organization_name}</span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {(permissions?.is_admin || permissions?.is_system_admin) && u.id !== user?.id ? (
                  <select
                    value={u.role}
                    onChange={(e) => handleChangeRole(u.id, e.target.value)}
                    className={`text-xs font-medium px-3 py-1.5 rounded-full cursor-pointer bg-slate-800 border border-slate-700 text-slate-300 focus:border-cyan-500`}
                    data-testid={`role-select-${u.id}`}
                  >
                    <option value="Admin">Administrator</option>
                    <option value="PowerUser">Superbruker</option>
                    <option value="Member">Medlem</option>
                  </select>
                ) : (
                  <span className={`text-xs font-medium px-3 py-1.5 rounded-full ${ROLE_LABELS[u.role]?.color}`}>
                    {ROLE_LABELS[u.role]?.label}
                  </span>
                )}
                
                {(permissions?.is_admin || permissions?.is_system_admin) && u.id !== user?.id && u.is_active && (
                  <button
                    onClick={() => handleDeactivate(u.id, u.name)}
                    className="text-rose-400 hover:text-rose-300 text-sm"
                    data-testid={`deactivate-${u.id}`}
                  >
                    Deaktiver
                  </button>
                )}
                
                {!u.is_active && (
                  <span className="text-xs text-rose-500">Deaktivert</span>
                )}
              </div>
            </div>
          ))}
          
          {users.length === 0 && (
            <div className="p-8 text-center text-slate-400">
              Ingen brukere funnet
            </div>
          )}
        </div>

        {/* Role Info */}
        <div className="mt-6 bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-2xl p-4">
          <h3 className="font-medium text-white mb-3">Roller og rettigheter</h3>
          <div className="space-y-2 text-sm text-slate-400">
            {permissions?.is_system_admin && (
              <div className="text-rose-400"><strong>Systemadministrator:</strong> Alle rettigheter på tvers av organisasjoner. Kan se systemstatistikk og administrere alle brukere.</div>
            )}
            <div><strong className="text-slate-200">Administrator:</strong> Full tilgang. Kan administrere brukere, innstillinger og abonnement.</div>
            <div><strong className="text-slate-200">Superbruker:</strong> Kan opprette og redigere oppgaver, avvik, og aktivere ISO/Miljøfyrtårn.</div>
            <div><strong className="text-slate-200">Medlem:</strong> Kan se oppgaver, fullføre egne oppgaver, og registrere avvik.</div>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Legg til bruker</h2>
            
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Navn</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="Ola Nordmann"
                  data-testid="new-user-name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">E-post</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="ola@bedrift.no"
                  data-testid="new-user-email"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Rolle</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-slate-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  data-testid="new-user-role"
                >
                  <option value="Member">Medlem</option>
                  <option value="PowerUser">Superbruker</option>
                  <option value="Admin">Administrator</option>
                </select>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-slate-300 hover:bg-slate-700 transition-colors"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 px-4 py-3 bg-cyan-500 text-slate-950 rounded-xl font-semibold hover:bg-cyan-400 disabled:opacity-50 transition-colors shadow-lg shadow-cyan-500/25"
                  data-testid="create-user-submit"
                >
                  {creating ? 'Oppretter...' : 'Opprett bruker'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppShell>
  );
}

export default UserManagement;
